export type CarType =
  | 'REGULAR'
  | 'SPORT'
  | 'PERFORMANCE'
  | 'SUPER'
  | 'EXOTIC'
  | 'HYPER';

export type MainUse =
  | 'WORK'
  | 'RACING'
  | 'TRANSPORT'
  | 'COLLECTIBLE'
  | 'INVESTMENT';

export interface Car {
  id: string;
  manufacturer: string;
  /** Model name on its own, e.g. "Murcielago". */
  model: string;
  countryOfOrigin: string;
  type: CarType;
  /** Top speed in miles per hour. */
  topSpeed: number;
  mainUse: MainUse;
  /** When the car was bought. ISO instant, truncate to a day to group. */
  datePurchased: string;
  /** When the car was last driven. Null if it has never been driven. */
  lastDriven: string | null;
  createdAt: string;
  updatedAt: string;
}

/** "Lamborghini Murcielago" from its parts. */
export const fullName = (car: Car) => `${car.manufacturer} ${car.model}`.trim();

const GRAPHQL_URL = process.env.REACT_APP_GRAPHQL_URL ?? '';
const API_KEY = process.env.REACT_APP_GRAPHQL_API_KEY ?? '';

export const isConfigured = () => Boolean(GRAPHQL_URL && API_KEY);

const CAR_FIELDS = `
  id
  manufacturer
  model
  countryOfOrigin
  type
  topSpeed
  mainUse
  datePurchased
  lastDriven
  createdAt
  updatedAt
`;

// Reads only.
//
// createCar, updateCar, and deleteCar are deliberately absent. They are
// annotated @aws_iam in the schema, so they need SigV4 signed credentials and
// will reject this API key. That is the point: the key below is visible to
// anyone who opens devtools, so it must not be able to write. Mutations run
// from the AppSync console or a credentialed script, never from the browser.
async function gql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  if (!isConfigured()) {
    throw new Error(
      'GraphQL is not configured. Set REACT_APP_GRAPHQL_URL and REACT_APP_GRAPHQL_API_KEY in .env'
    );
  }

  const res = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    throw new Error(`GraphQL request failed with ${res.status}`);
  }

  const json = await res.json();

  // AppSync returns 200 with an errors array rather than an HTTP error, so
  // this has to be checked explicitly or failures look like empty results.
  if (json.errors?.length) {
    throw new Error(json.errors[0]?.message ?? 'GraphQL error');
  }

  return json.data as T;
}

export const listCars = () =>
  gql<{ listCars: Car[] }>(`query ListCars { listCars { ${CAR_FIELDS} } }`).then(
    (d) => d.listCars
  );

export const getCar = (id: string) =>
  gql<{ getCar: Car | null }>(
    `query GetCar($id: ID!) { getCar(id: $id) { ${CAR_FIELDS} } }`,
    { id }
  ).then((d) => d.getCar);
